import {
  CohortBuilderItemChangeStatusLegacy,
  type CohortBuilderDefinitionLegacy,
} from '@verantos-dev/types-cohorts';

import { checkStudyPeriodRestrictionsStatus } from './checkStudyPeriodRestrictionsStatus';
import { computeDiffForCohortBuilderItem } from './computeDiffForCohortBuilderItem';

/**
 * Calculates which index date, subject variables, simple events, and derived events have changed.
 * Returns a new CohortBuilderDefinition with the changes indicated on each.
 * Cases are CohortBuilderItemChangeStatus:
 *  NEW = 'New',
 *  EDITED = 'Edited'
 *  DELETED = 'Deleted',
 *  UNCHANGED = 'Unchanged'
 */
export const addChangeStatusToCohortBuilderItems = (
  prevCohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  nextCohortDefinition: CohortBuilderDefinitionLegacy
): {
  cohortDefinitionWithStatuses: CohortBuilderDefinitionLegacy;
  updatedCohortItems: string[];
} => {
  const prevIndexDate = prevCohortDefinition?.indexDate;
  const nextIndexDate = nextCohortDefinition.indexDate;

  const prevSubjectVariables = prevCohortDefinition?.subjectVariables;
  const nextSubjectVariables = nextCohortDefinition.subjectVariables;

  const prevSimpleEvents = prevCohortDefinition?.simpleEvents;
  const nextSimpleEvents = nextCohortDefinition.simpleEvents;

  const prevDerivedEvents = prevCohortDefinition?.derivedEvents;
  const nextDerivedEvents = nextCohortDefinition.derivedEvents;

  const updatedCohortItems = [
    // ids of items that were removed
    ...(prevIndexDate && !nextIndexDate ? [prevIndexDate.id] : []),
    ...(prevSubjectVariables
      ?.filter(
        (prevSubjectVariable) =>
          !nextSubjectVariables.some(
            (nextSubjectVariable) => nextSubjectVariable.id === prevSubjectVariable.id
          )
      )
      .map((prevSubjectVariable) => prevSubjectVariable.id) || []),
    ...(prevSimpleEvents
      ?.filter(
        (prevSimpleEvent) =>
          !nextSimpleEvents.some((nextSimpleEvent) => nextSimpleEvent.id === prevSimpleEvent.id)
      )
      .map((prevSimpleEvent) => prevSimpleEvent.id) || []),
    ...(prevDerivedEvents
      ?.filter(
        (prevDerivedEvent) =>
          !nextDerivedEvents.some((nextDerivedEvent) => nextDerivedEvent.id === prevDerivedEvent.id)
      )
      .map((prevDerivedEvent) => prevDerivedEvent.id) || []),
  ];

  let nextIndexDateChangeStatus: CohortBuilderItemChangeStatusLegacy | undefined;
  if (nextIndexDate) {
    // change status if index date exists with current update
    if (!prevIndexDate) {
      // if no index date previously existed, then it's new
      nextIndexDateChangeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
    } else {
      // index date previously existed
      if (computeDiffForCohortBuilderItem(prevIndexDate, nextIndexDate)) {
        // if index date previously existed and is different from current update, then it's edited

        if (prevIndexDate.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW) {
          // if index date was new, then it's still new
          nextIndexDateChangeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
          updatedCohortItems.push(nextIndexDate.id);
        } else {
          // index date is edited
          nextIndexDateChangeStatus = CohortBuilderItemChangeStatusLegacy.EDITED;
          updatedCohortItems.push(nextIndexDate.id);
        }
      } else {
        // if index date previously existed and is the same as current update, then it's unchanged
        nextIndexDateChangeStatus = prevIndexDate.changeStatus;
      }
    }
  }

  const updatedCohortDefinition: CohortBuilderDefinitionLegacy = {
    ...nextCohortDefinition,
    indexDate: nextIndexDate
      ? {
          ...nextIndexDate,
          changeStatus: nextIndexDateChangeStatus ?? CohortBuilderItemChangeStatusLegacy.UNCHANGED,
        }
      : undefined,
    subjectVariables: nextSubjectVariables.map((nextSubjectVariable) => {
      const prevSubjectVariable = prevSubjectVariables?.find(
        (prevSubjectVariable) => prevSubjectVariable.id === nextSubjectVariable.id
      );

      let _subjectVariableChangeStatus: CohortBuilderItemChangeStatusLegacy | undefined;
      if (prevSubjectVariable) {
        // subject variable previously existed
        if (computeDiffForCohortBuilderItem(prevSubjectVariable, nextSubjectVariable)) {
          // if subject variable previously existed and is different from current update
          if (prevSubjectVariable.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW) {
            // if subject variable was new, then it's still new
            _subjectVariableChangeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
            updatedCohortItems.push(nextSubjectVariable.id);
          } else {
            // subject variable is edited
            _subjectVariableChangeStatus = CohortBuilderItemChangeStatusLegacy.EDITED;
            updatedCohortItems.push(nextSubjectVariable.id);
          }
        } else {
          // if subject variable previously existed and is the same as current update, then it's unchanged
          _subjectVariableChangeStatus = prevSubjectVariable.changeStatus;
        }
      } else {
        // subject variable is new
        _subjectVariableChangeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
      }

      return {
        ...nextSubjectVariable,
        changeStatus: _subjectVariableChangeStatus,
      };
    }),
    simpleEvents: nextSimpleEvents.map((nextSimpleEvent) => {
      const prevSimpleEvent = prevSimpleEvents?.find(
        (prevSimpleEvent) => prevSimpleEvent.id === nextSimpleEvent.id
      );

      let _simpleEventChangeStatus: CohortBuilderItemChangeStatusLegacy | undefined;
      if (prevSimpleEvent) {
        // simple event previously existed
        if (computeDiffForCohortBuilderItem(prevSimpleEvent, nextSimpleEvent)) {
          // if simple event previously existed and is different from current update
          if (prevSimpleEvent.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW) {
            // if simple event was new, then it's still new
            _simpleEventChangeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
            updatedCohortItems.push(nextSimpleEvent.id);
          } else {
            // simple event is edited
            _simpleEventChangeStatus = CohortBuilderItemChangeStatusLegacy.EDITED;
            updatedCohortItems.push(nextSimpleEvent.id);
          }
        } else {
          // if simple event previously existed and is the same as current update, then it's unchanged
          _simpleEventChangeStatus = prevSimpleEvent.changeStatus;
        }
      } else {
        // simple event is new
        _simpleEventChangeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
      }

      return {
        ...nextSimpleEvent,
        changeStatus: _simpleEventChangeStatus,
      };
    }),
    derivedEvents: nextDerivedEvents.map((nextDerivedEvent) => {
      const prevDerivedEvent = prevDerivedEvents?.find(
        (prevDerivedEvent) => prevDerivedEvent.id === nextDerivedEvent.id
      );

      let _derivedEventChangeStatus: CohortBuilderItemChangeStatusLegacy | undefined;
      if (prevDerivedEvent) {
        // derived event previously existed
        if (computeDiffForCohortBuilderItem(prevDerivedEvent, nextDerivedEvent)) {
          // if derived event previously existed and is different from current update
          if (prevDerivedEvent.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW) {
            // if derived event was new, then it's still new
            _derivedEventChangeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
            updatedCohortItems.push(nextDerivedEvent.id);
          } else {
            // derived event is edited
            _derivedEventChangeStatus = CohortBuilderItemChangeStatusLegacy.EDITED;
            updatedCohortItems.push(nextDerivedEvent.id);
          }
        } else {
          // if derived event previously existed and is the same as current update, then it's unchanged
          _derivedEventChangeStatus = prevDerivedEvent.changeStatus;
        }
      } else {
        // derived event is new
        _derivedEventChangeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
      }

      return {
        ...nextDerivedEvent,
        changeStatus: _derivedEventChangeStatus,
      };
    }),
    studyPeriodRestrictions: {
      overallStudyPeriodRestriction:
        nextCohortDefinition?.studyPeriodRestrictions?.overallStudyPeriodRestriction,
      baselinePeriodRestriction:
        nextCohortDefinition?.studyPeriodRestrictions?.baselinePeriodRestriction,
      followUpPeriodRestriction:
        nextCohortDefinition?.studyPeriodRestrictions?.followUpPeriodRestriction,
      changeStatus: checkStudyPeriodRestrictionsStatus(
        prevCohortDefinition?.studyPeriodRestrictions,
        nextCohortDefinition?.studyPeriodRestrictions
      ),
    },
  };

  return {
    cohortDefinitionWithStatuses: updatedCohortDefinition,
    updatedCohortItems,
  };
};
